import { useEffect, useState } from "react";
import { api, CATEGORY_IMAGES } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Sprout, Flower2, Trees, ArrowRight } from "lucide-react";

const STAGE_META = [
    { min: 0, key: "seed", label: "Seed", Icon: Sprout, hint: "Fertile ground, awaiting your care." },
    { min: 25, key: "seedling", label: "Seedling", Icon: Sprout, hint: "The first tender leaves are unfurling." },
    { min: 50, key: "sapling", label: "Growing", Icon: Flower2, hint: "Roots are deepening; growth is steady." },
    { min: 75, key: "flourishing", label: "Flourishing", Icon: Trees, hint: "Fruit is visible; keep watering what is alive." },
];

const stageFor = (score) => {
    let s = STAGE_META[0];
    for (const m of STAGE_META) if (score >= m.min) s = m;
    return s;
};

export default function Timeline() {
    const [history, setHistory] = useState([]);
    const [retake, setRetake] = useState(null);
    const [loading, setLoading] = useState(true);
    const [bpLibrary, setBpLibrary] = useState([]);

    useEffect(() => {
        (async () => {
            const [h, r] = await Promise.all([
                api.get("/assessment/history"),
                api.get("/assessment/retake-status"),
            ]);
            setHistory(h.data);
            setRetake(r.data);
            setLoading(false);
        })();
        api.get("/blueprint/history").then((r) => setBpLibrary(r.data?.blueprints || [])).catch(() => {});
    }, []);

    if (loading) return <div className="text-muted-foreground py-16">Opening your timeline…</div>;

    const latest = history[0];

    return (
        <div className="space-y-16">
            <header className="max-w-3xl">
                <div className="eyebrow-gold mb-4">Growth history</div>
                <h1 className="font-serif text-5xl md:text-7xl leading-[1.02]">Remember His faithfulness.</h1>
                <p className="mt-6 text-muted-foreground text-lg leading-relaxed">
                    Every 90 days, retake the assessment and see how the garden entrusted to you has grown across
                    every area. The goal here is not performance — it is remembering God's faithfulness and
                    recognizing growth.
                </p>
            </header>

            {/* Retake card */}
            {retake && (
                <div className="hairline rounded-md p-8 md:p-10 flex flex-wrap gap-6 items-center justify-between" data-testid="retake-card">
                    <div>
                        <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground mb-2">
                            {retake.can_retake ? "You may retake the Blueprint" : "Next recommended retake"}
                        </div>
                        <div className="font-serif text-3xl md:text-4xl">
                            {retake.can_retake
                                ? "Ninety days have passed."
                                : `In ${retake.days_until_recommended} days.`}
                        </div>
                        {retake.days_since != null && (
                            <div className="text-sm text-muted-foreground mt-2">
                                It has been {retake.days_since} day{retake.days_since === 1 ? "" : "s"} since your last blueprint.
                            </div>
                        )}
                    </div>
                    <Link to="/assessment" data-testid="retake-btn">
                        <Button
                            disabled={!retake.can_retake}
                            className="bg-[hsl(var(--eden-forest))] text-[hsl(var(--eden-cream))] hover:bg-[hsl(var(--eden-forest))]/90 rounded-md h-11 px-6"
                        >
                            {retake.can_retake ? "Retake the Blueprint" : "Not yet"} <ArrowRight className="w-4 h-4 ml-2" strokeWidth={1.5} />
                        </Button>
                    </Link>
                </div>
            )}

            {/* Growth per garden */}
            {latest && (
                <section>
                    <div className="mb-8">
                        <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground mb-2">Growth by garden</div>
                        <div className="font-serif text-3xl md:text-4xl">Each garden, at a glance.</div>
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                        {latest.categories.map((c) => {
                            const trail = history.map((h) => (h.categories || []).find((cc) => cc.key === c.key)?.score ?? 0).reverse();
                            const stage = stageFor(c.score);
                            const Icon = stage.Icon;
                            return (
                                <motion.div
                                    key={c.key}
                                    initial={{ opacity: 0, y: 12 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.6 }}
                                    className="hairline rounded-md overflow-hidden"
                                    data-testid={`timeline-garden-${c.key}`}
                                >
                                    <div className="relative aspect-[16/6] overflow-hidden">
                                        <img src={CATEGORY_IMAGES[c.key]} alt={c.label} className="absolute inset-0 w-full h-full object-cover" />
                                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                                        <div className="absolute inset-0 p-5 flex flex-col justify-between text-white">
                                            <div className="flex items-center justify-between">
                                                <span className="text-[10px] uppercase tracking-[0.3em]">{stage.label}</span>
                                                <span className="font-serif text-2xl">{c.score}</span>
                                            </div>
                                            <div className="font-serif text-3xl">{c.label}</div>
                                        </div>
                                    </div>
                                    <div className="p-6">
                                        <div className="flex items-center gap-3 mb-4">
                                            <Icon className="w-5 h-5 text-[hsl(var(--eden-gold))]" strokeWidth={1.5} />
                                            <div className="text-sm text-muted-foreground">{stage.hint}</div>
                                        </div>
                                        <MiniSparkline values={trail} />
                                        <div className="mt-3 flex justify-between text-[10px] uppercase tracking-widest text-muted-foreground">
                                            <span>Earliest</span>
                                            <span>Latest</span>
                                        </div>
                                    </div>
                                </motion.div>
                            );
                        })}
                    </div>
                </section>
            )}

            {/* Blueprint Library */}
            {bpLibrary.length > 0 && (
                <section data-testid="blueprint-library-section">
                    <div className="mb-6">
                        <div className="eyebrow-gold mb-2">Blueprint Library</div>
                        <div className="font-serif text-3xl md:text-4xl">Every Blueprint you have received.</div>
                        <p className="mt-3 text-sm text-muted-foreground max-w-2xl leading-relaxed">
                            Look back across seasons and see how God has worked. Each Blueprint captures how your one
                            garden was standing on the day it was composed.
                        </p>
                    </div>
                    <div className="space-y-3">
                        {bpLibrary.map((b) => (
                            <div key={b.blueprint_id} className="hairline rounded-md p-5 md:p-6" data-testid={`blueprint-history-${b.blueprint_id}`}>
                                <div className="flex flex-wrap items-baseline justify-between gap-3">
                                    <div className="eyebrow-gold">
                                        {new Date(b.created_at).toLocaleString(undefined, { dateStyle: "long" })}
                                    </div>
                                    <div className="font-serif text-2xl">Overall {b.overall_score}</div>
                                </div>
                                {b.overall_summary && (
                                    <p className="mt-3 text-sm leading-relaxed text-muted-foreground italic">
                                        &ldquo;{b.overall_summary}{b.overall_summary.length >= 280 ? "…" : ""}&rdquo;
                                    </p>
                                )}
                                <div className="mt-4 flex flex-wrap gap-2 items-center">
                                    {(b.strongest_gardens || []).slice(0, 3).map((g) => (
                                        <span key={g} className="text-[10px] uppercase tracking-[0.25em] px-2.5 py-1 rounded-md border border-[hsl(var(--eden-olive))]/40 text-[hsl(var(--eden-olive))]">
                                            {g} · steady
                                        </span>
                                    ))}
                                    {(b.priority_gardens || []).slice(0, 3).map((g) => (
                                        <span key={g} className="text-[10px] uppercase tracking-[0.25em] px-2.5 py-1 rounded-md border border-[hsl(var(--eden-gold))]/40 text-[hsl(var(--eden-gold))]">
                                            {g} · cultivating
                                        </span>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </section>
            )}

            {/* Assessment history */}
            <section>
                <div className="mb-6">
                    <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground mb-2">History</div>
                    <div className="font-serif text-3xl md:text-4xl">Every blueprint you've walked through.</div>
                </div>
                <div className="space-y-3">
                    {history.map((h, i) => (
                        <div key={h.assessment_id} className="hairline rounded-md p-5 md:p-6 flex flex-wrap gap-4 items-center justify-between" data-testid={`history-${i}`}>
                            <div>
                                <div className="text-xs uppercase tracking-widest text-muted-foreground">
                                    {new Date(h.created_at).toLocaleString(undefined, { dateStyle: "long" })}
                                </div>
                                <div className="font-serif text-2xl mt-1">Overall {h.overall_score}</div>
                            </div>
                            <div className="flex flex-wrap gap-2">
                                {(h.categories || []).map((c) => (
                                    <span key={c.key} className="text-xs px-3 py-1.5 rounded-md bg-foreground/[0.05] border border-foreground/10">
                                        {c.label} · {c.score}
                                    </span>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </section>
        </div>
    );
}

const MiniSparkline = ({ values }) => {
    if (!values || values.length === 0) return null;
    const w = 240, h = 40;
    const max = 100, min = 0;
    const n = values.length;
    if (n === 1) {
        // Single point → show a bar
        const v = values[0];
        return (
            <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-10">
                <line x1="0" y1={h - 1} x2={w} y2={h - 1} stroke="currentColor" strokeOpacity="0.1" />
                <circle cx={w / 2} cy={h - (v / max) * h} r="3" fill="hsl(var(--eden-gold))" />
            </svg>
        );
    }
    const pts = values.map((v, i) => {
        const x = (i / (n - 1)) * (w - 6) + 3;
        const y = h - ((v - min) / (max - min)) * (h - 6) - 3;
        return [x, y];
    });
    const path = pts.map(([x, y], i) => `${i ? "L" : "M"}${x.toFixed(1)},${y.toFixed(1)}`).join(" ");
    return (
        <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-10">
            <path d={path} fill="none" stroke="hsl(var(--eden-gold))" strokeWidth="1.5" />
            {pts.map(([x, y], i) => (
                <circle key={i} cx={x} cy={y} r={i === pts.length - 1 ? 3 : 1.5} fill="hsl(var(--eden-gold))" />
            ))}
        </svg>
    );
};
